// Modal de cambio de contraseña (misma página, sin abrir otra pestaña)
// Usado desde Dashboard, Team, Service Tickets, Journey, etc.
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getApiUrl } from '../config/api';
import 'bootstrap-icons/font/bootstrap-icons.css';

const validateSecurePassword = (pwd, currentPwd = '') => {
  const err = [];
  if (!pwd || pwd.length < 8) err.push('Al menos 8 caracteres');
  if (pwd.length > 128) err.push('Máximo 128 caracteres');
  if (/\s/.test(pwd)) err.push('Sin espacios');
  if (!/[A-Z]/.test(pwd)) err.push('Al menos una mayúscula');
  if (!/[a-z]/.test(pwd)) err.push('Al menos una minúscula');
  if (!/[0-9]/.test(pwd)) err.push('Al menos un número');
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) err.push('Al menos un carácter especial');
  if (currentPwd && pwd === currentPwd) err.push('Debe ser distinta a la actual');
  return err;
};

const getPasswordRequirementChecks = (pwd, currentPwd = '') => ({
  minLength: !!pwd && pwd.length >= 8,
  maxLength: !!pwd && pwd.length <= 128,
  noSpaces: !!pwd && !/\s/.test(pwd),
  hasUpper: !!pwd && /[A-Z]/.test(pwd),
  hasLower: !!pwd && /[a-z]/.test(pwd),
  hasNumber: !!pwd && /[0-9]/.test(pwd),
  hasSpecial: !!pwd && /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
  differentFromCurrent: !currentPwd || (pwd && pwd !== currentPwd)
});

export default function ChangePasswordModal({ open, onClose }) {
  const [formData, setFormData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const passwordChecks = getPasswordRequirementChecks(formData.newPassword, formData.currentPassword);
  const confirmTouched = formData.confirmPassword.length > 0;
  const passwordsMatch =
    !!formData.newPassword &&
    !!formData.confirmPassword &&
    formData.newPassword === formData.confirmPassword;

  useEffect(() => {
    if (!open) {
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setError('');
      setSuccess(false);
    }
  }, [open]);

  /** Cerrar con Escape o mantener accesible desde cualquier estado */
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const { currentPassword, newPassword, confirmPassword } = formData;
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Todos los campos son obligatorios.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('La nueva contraseña y la confirmación no coinciden.');
      return;
    }
    const errs = validateSecurePassword(newPassword, currentPassword);
    if (errs.length) {
      setError('Nueva contraseña: ' + errs.join(', '));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(getApiUrl('USER_CHANGE_PASSWORD'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.result || 'Error al cambiar la contraseña.');
        setLoading(false);
        return;
      }
      setSuccess(true);
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      try {
        if (data.new_token) {
          sessionStorage.setItem('auth_token', data.new_token);
          window.dispatchEvent(new Event('realtime:token_set'));
        }
      } catch (_) {}
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Error de conexión. Intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const modalContent = (
    <div
      className="change-password-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="change-password-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="change-password-modal-inner">
          <div className="change-password-modal-header">
            <h2 id="change-password-modal-title" className="change-password-modal-title">Cambiar contraseña</h2>
            <button
              type="button"
              className="change-password-modal-close"
              onClick={onClose}
              aria-label="Cerrar"
              title="Cerrar"
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>
          <p className="change-password-modal-subtitle">Ingrese su contraseña actual y la nueva contraseña.</p>
          {success ? (
            <div className="change-password-modal-success">
              <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
              <strong>Contraseña actualizada correctamente.</strong>
              <button type="button" className="change-password-modal-close-btn" onClick={onClose}>
                Cerrar
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="change-password-modal-form">
              <div className="change-password-modal-field">
                <label className="change-password-modal-label">Contraseña actual</label>
                <input
                  type="password"
                  className="change-password-modal-input"
                  placeholder="Contraseña actual"
                  autoComplete="current-password"
                  value={formData.currentPassword}
                  onChange={e => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  required
                />
              </div>
              <div className="change-password-modal-field">
                <label className="change-password-modal-label">Nueva contraseña</label>
                <input
                  type="password"
                  className="change-password-modal-input"
                  placeholder="Nueva contraseña"
                  autoComplete="new-password"
                  value={formData.newPassword}
                  onChange={e => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                  required
                />
              </div>
              <div className="change-password-modal-field">
                <label className="change-password-modal-label">Confirmar nueva contraseña</label>
                <input
                  type="password"
                  className="change-password-modal-input"
                  placeholder="Confirmar nueva contraseña"
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={e => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                />
              </div>
              <p className={`change-password-modal-match ${confirmTouched ? (passwordsMatch ? 'ok' : 'bad') : ''}`}>
                {confirmTouched
                  ? (passwordsMatch ? 'Las contraseñas son iguales.' : 'Las contraseñas no coinciden.')
                  : 'Confirma tu nueva contraseña para comparar.'}
              </p>
              <div className="change-password-modal-rules">
                <p className="change-password-modal-rules-title">Estándar de contraseña:</p>
                <ul className="change-password-modal-rules-list">
                  <li className={passwordChecks.minLength ? 'ok' : 'bad'}>Mínimo 8 caracteres</li>
                  <li className={passwordChecks.maxLength ? 'ok' : 'bad'}>Máximo 128 caracteres</li>
                  <li className={passwordChecks.noSpaces ? 'ok' : 'bad'}>Sin espacios</li>
                  <li className={passwordChecks.hasUpper ? 'ok' : 'bad'}>Al menos una mayúscula</li>
                  <li className={passwordChecks.hasLower ? 'ok' : 'bad'}>Al menos una minúscula</li>
                  <li className={passwordChecks.hasNumber ? 'ok' : 'bad'}>Al menos un número</li>
                  <li className={passwordChecks.hasSpecial ? 'ok' : 'bad'}>Al menos un carácter especial</li>
                  <li className={passwordChecks.differentFromCurrent ? 'ok' : 'bad'}>Debe ser distinta a la actual</li>
                </ul>
              </div>
              <button type="submit" className="change-password-modal-submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Actualizando...
                  </>
                ) : (
                  'Cambiar contraseña'
                )}
              </button>
              {error && (
                <div className="change-password-modal-error">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
